
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { LogOut, ChevronDown, Shield, Camera, UserIcon, Settings, Beaker } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { NotificationIndicator } from "@/components/NotificationIndicator";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import { useTranslation } from "@/hooks/useTranslation";
import { useDarkroomMode } from "@/hooks/useDarkroomMode";
import { cn } from "@/lib/utils";

interface UserDropdownMenuProps {
  user: SupabaseUser;
  isAdmin: boolean;
}

export function UserDropdownMenu({ user, isAdmin }: UserDropdownMenuProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { darkroomMode } = useDarkroomMode();
  
  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
      
      toast.success(t('sign_out_success'));
      
      // Force navigate to landing page
      window.location.href = '/';
    } catch (error: any) {
      console.error("Sign out error:", error);
      toast.error(error.message || "Failed to sign out");
    }
  };
  
  return (
    <div className="flex items-center gap-4">
      <NotificationIndicator />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            className={cn(
              "gap-2",
              darkroomMode && "hover:bg-[#481218] hover:text-[#ea384c]"
            )}
          >
            {user.user_metadata.username || user.email}
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem asChild>
            <Link to="/profile" className="flex items-center">
              <UserIcon className="mr-2 h-4 w-4" />
              {t('profile')}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/inventory" className="flex items-center">
              <Camera className="mr-2 h-4 w-4" />
              {t('inventory')}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/develop" className="flex items-center">
              <Beaker className="mr-2 h-4 w-4" />
              {t('develop')}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/settings" className="flex items-center">
              <Settings className="mr-2 h-4 w-4" />
              {t('settings')}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {isAdmin && (
            <>
              <DropdownMenuItem asChild>
                <Link to="/admin" className="flex items-center">
                  <Shield className="mr-2 h-4 w-4" />
                  {t('admin')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleSignOut} className="text-red-600">
            <LogOut className="mr-2 h-4 w-4" />
            {t('sign_out')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
